import React, { useState } from 'react';
import {
    Box, Grid, Button, Tooltip,
    InputAdornment, InputLabel, MenuItem, Paper, Select, TextField, Typography, useTheme, IconButton, FormControl,
    Tabs, Tab
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { FieldArray, Formik } from 'formik';
import * as yup from 'yup';
import { tokens } from "../../../theme";

// Existing hooks
import { useAddOrganizationS3ConfigMutation } from '../../../features/organizations/organizationsApiSlice';
// New placeholder hook for “connect existing bucket”
import { useConnectExistingBucketMutation } from '../../../features/organizations/organizationsApiSlice';

const AWSOnboardingWizard = ({ organization }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Original mutation for the “Create Bucket” tab
    const [addOrganizationS3Config] = useAddOrganizationS3ConfigMutation();
    // Placeholder mutation for “Connect Existing Bucket”
    const [connectExistingBucket] = useConnectExistingBucketMutation(); //[placeholder]

    const [isSubmitted, setIsSubmitted] = useState(false);

    // Track which tab is active: 0 = Connect Existing, 1 = Create Bucket
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        // Reset submission state if we switch tabs
        setIsSubmitted(false);
    };

    // ---------- “Connect Existing Bucket” form logic ----------
    const [existingBucketInput, setExistingBucketInput] = useState("");
    const [connectSuccess, setConnectSuccess] = useState(false);

    const [connectErrorMessage, setConnectErrorMessage] = useState("");

    const handleConnectExistingBucket = async () => {
        setConnectErrorMessage(""); // Clear old errors
        try {
            let cleanedInput = existingBucketInput.trim();
    
            // Normalize s3://bucket//prefix// to s3://bucket/prefix
            if (cleanedInput.startsWith('s3://')) {
                const parts = cleanedInput.replace(/^s3:\/\//, '').split('/');
                const bucket = parts[0];
                const prefix = parts.slice(1).filter(Boolean).join('/'); // Remove empty segments
                cleanedInput = `s3://${bucket}${prefix ? '/' + prefix : ''}`;
            } else {
                cleanedInput = cleanedInput.replace(/\/+$/, ''); // Strip trailing slashes for bucket-only input
            }
    
            await connectExistingBucket({
                id: organization.organization_id,
                bucketNameOrUri: cleanedInput
            }).unwrap();
    
            setConnectSuccess(true);
        } catch (error) {
            console.error("Failed to connect existing bucket:", error);
            setConnectSuccess(false);
    
            if (error?.status === 404) {
                setConnectErrorMessage("The specified bucket does not exist. Please double check the name.");
            } else if (error?.status === 400) {
                setConnectErrorMessage("The specified location is not empty. Please select a different bucket or empty prefix.");
            } else {
                setConnectErrorMessage("An unexpected error occurred while connecting. Please try again.");
            }
        }
    };
    
    // ---------- “Create Bucket” form logic (original) ----------
    const defaultBucketArn = organization.name.replace(/[\W_]+/g, '-').toLowerCase();
    const initialOnpremConfig = organization.s3_config?.infra_data?.onprem_config || {
        onprem_bucket: defaultBucketArn,
        onprem_service_user_enabled: false,
        foreign_service_users: [],
        foreign_bucket_arns: [],
    };

    const initialValues = {
        infra_data: {
            onprem_config: initialOnpremConfig,
        },
    };

    const validationSchema = yup.object({
        infra_data: yup.object({
            onprem_config: yup.object({
                onprem_bucket: yup.string().required('Bucket Name is required'),
            }),
        }),
    });

    const handleSubmitCreateBucket = async (values) => {
        const s3Config = values.infra_data;
        try {
            await addOrganizationS3Config({ id: organization.organization_id, s3Config });
            setIsSubmitted(true);
        } catch (error) {
            console.error("Failed to add S3 configuration:", error);
        }
    };

    const InfoIconWithTooltip = ({ title }) => (
        <Tooltip title={title} arrow>
            <IconButton>
                <InfoOutlined
                    fontSize="small"
                    sx={{
                        color: colors.primary[100],
                        '&:hover': { transform: 'scale(1.2)' }
                    }}
                />
            </IconButton>
        </Tooltip>
    );

    return (
        <Paper
            elevation={3}
            sx={{
                background: `linear-gradient(135deg, ${colors.primary[400]}, ${colors.primary[800]})`,
                borderRadius: '20px',
                padding: '20px',
            }}
        >
            <Typography variant="h4" mb={4} color="textPrimary">
                5x5 Coop AWS Onboarding
            </Typography>

            {/* Tabs */}
            <Tabs value={tabValue} onChange={handleTabChange} textColor="inherit" indicatorColor="secondary">
                <Tab label="Connect Existing S3 Location" />
                <Tab label="Create New S3 (Coming Soon)" disabled={false} />
            </Tabs>


            {/* Tab Panel: Connect Existing Bucket */}
            {tabValue === 0 && (
                <Box mt={2}>
                    {!connectSuccess ? (
                        <>
                            <Typography variant="h6" mb={2} color="textPrimary">
                                Connect Existing S3 Bucket
                            </Typography>
                            <TextField
                                fullWidth
                                variant="filled"
                                label="S3 Bucket Name or URI"
                                value={existingBucketInput}
                                onChange={(e) => setExistingBucketInput(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <Typography variant="body2" color="textSecondary" mb={2}>
                                You can enter either a full S3 URI (e.g., <code>s3://shared-client-bucket/coop/org-abc/</code>)
                                or just a bucket name. This will become the delivery root for your organization’s data.
                            </Typography>
                            {connectErrorMessage && (
                                <Typography variant="body2" color="error" mb={2}>
                                    {connectErrorMessage}
                                </Typography>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConnectExistingBucket}
                            >
                                Connect Location
                            </Button>

                        </>
                    ) : (
                        <Box textAlign="center" mt={4}>
                            <Typography variant="h5" color="textPrimary">
                                Existing Bucket Connected
                            </Typography>
                            <Typography variant="body1" color="textSecondary" mt={2}>
                                We have successfully connected your existing S3 bucket.
                                Please refresh or check back for further configuration updates.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Tab Panel: Create Bucket */}
            {tabValue === 1 && (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmitCreateBucket}
                >
                    {(formik) => (
                        <form onSubmit={formik.handleSubmit}>
                            {!isSubmitted ? (
                                <Box mt={3}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} display="flex" alignItems="center">
                                            <TextField
                                                fullWidth
                                                variant="filled"
                                                type="text"
                                                label="Trovo Bucket Name"
                                                {...formik.getFieldProps('infra_data.onprem_config.onprem_bucket')}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment
                                                            position="start"
                                                            style={{ marginRight: "0px" }}
                                                        >
                                                            <div
                                                                style={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    fontWeight: 'bold',
                                                                }}
                                                            >
                                                                trovo-coop-
                                                            </div>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <InfoIconWithTooltip title="This is the bucket name for your organization in our system. You can change the part after 'trovo-coop-' if you'd like." />
                                        </Grid>
                                        <Grid item xs={12} display="flex" alignItems="center">
                                            <FormControl fullWidth variant="filled">
                                                <InputLabel htmlFor="onprem_service_user_enabled">Onprem Service User</InputLabel>
                                                <Select
                                                    id="onprem_service_user_enabled"
                                                    name="infra_data.onprem_config.onprem_service_user_enabled"
                                                    value={formik.values.infra_data.onprem_config.onprem_service_user_enabled}
                                                    onChange={(event) =>
                                                        formik.setFieldValue(
                                                            'infra_data.onprem_config.onprem_service_user_enabled',
                                                            event.target.value
                                                        )
                                                    }
                                                    onBlur={formik.handleBlur}
                                                    label="Onprem Service User"
                                                >
                                                    <MenuItem value={true}>TRUE</MenuItem>
                                                    <MenuItem value={false}>FALSE</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <InfoIconWithTooltip title="On-prem Service User is only used if your company doesn't have S3. Selecting this will require engineering intervention to retrieve those credentials." />
                                        </Grid>
                                        {/* foreign_service_users section */}
                                        <Grid item xs={12}>
                                            <Paper
                                                elevation={3}
                                                sx={{ padding: '20px', borderRadius: '10px' }}
                                            >
                                                <FieldArray name="infra_data.onprem_config.foreign_service_users">
                                                    {({ push, remove }) => (
                                                        <Box>
                                                            <Box display="flex" alignItems="center">
                                                                <Typography variant="h6">
                                                                    Add Foreign AWS ARNs
                                                                </Typography>
                                                                <InfoIconWithTooltip title="These are roles or users from other AWS accounts (our customers) that will be able to access the buckets." />
                                                            </Box>
                                                            {formik.values.infra_data.onprem_config.foreign_service_users.map((user, index) => (
                                                                <Box
                                                                    key={index}
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    mb={2}
                                                                >
                                                                    <Box mr={2} flexBasis="80%">
                                                                        <TextField
                                                                            fullWidth
                                                                            variant="filled"
                                                                            type="text"
                                                                            label="ARN"
                                                                            name={`infra_data.onprem_config.foreign_service_users.${index}.arn`}
                                                                            value={user.arn}
                                                                            onChange={formik.handleChange}
                                                                            onBlur={formik.handleBlur}
                                                                        />
                                                                    </Box>
                                                                    <Button
                                                                        variant="outlined"
                                                                        style={{
                                                                            color: colors.redAccent[500],
                                                                            borderColor: colors.redAccent[500]
                                                                        }}
                                                                        onClick={() => remove(index)}
                                                                    >
                                                                        Delete
                                                                    </Button>
                                                                </Box>
                                                            ))}
                                                            <Button
                                                                variant="outlined"
                                                                style={{
                                                                    color: colors.greenAccent[500],
                                                                    borderColor: colors.greenAccent[500]
                                                                }}
                                                                onClick={() => push({ arn: '' })}
                                                            >
                                                                <Box component="span" fontSize="20px">+</Box> Add ARN
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </FieldArray>
                                            </Paper>
                                        </Grid>
                                        {/* foreign_bucket_arns section */}
                                        <Grid item xs={12}>
                                            <Paper
                                                elevation={3}
                                                sx={{ padding: '20px', borderRadius: '10px', marginTop: '20px' }}
                                            >
                                                <FieldArray name="infra_data.onprem_config.foreign_bucket_arns">
                                                    {({ push, remove }) => (
                                                        <Box>
                                                            <Box display="flex" alignItems="center">
                                                                <Typography variant="h6">
                                                                    Foreign Bucket ARNs
                                                                </Typography>
                                                                <InfoIconWithTooltip title="These are any buckets in the customer account that we want to set up a role for our users to access. This is applicable if they plan on providing us data in S3." />
                                                            </Box>
                                                            {formik.values.infra_data.onprem_config.foreign_bucket_arns.map((bucketArn, index) => (
                                                                <Box
                                                                    key={index}
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    mb={2}
                                                                >
                                                                    <Box mr={2} flexBasis="80%">
                                                                        <TextField
                                                                            fullWidth
                                                                            variant="filled"
                                                                            type="text"
                                                                            label="Bucket ARN"
                                                                            name={`infra_data.onprem_config.foreign_bucket_arns.${index}`}
                                                                            value={bucketArn}
                                                                            onChange={formik.handleChange}
                                                                            onBlur={formik.handleBlur}
                                                                        />
                                                                    </Box>
                                                                    <Button
                                                                        variant="outlined"
                                                                        style={{
                                                                            color: colors.redAccent[500],
                                                                            borderColor: colors.redAccent[500]
                                                                        }}
                                                                        onClick={() => remove(index)}
                                                                    >
                                                                        Delete
                                                                    </Button>
                                                                </Box>
                                                            ))}
                                                            <Button
                                                                variant="outlined"
                                                                style={{
                                                                    color: colors.greenAccent[500],
                                                                    borderColor: colors.greenAccent[500]
                                                                }}
                                                                onClick={() => push('')}
                                                            >
                                                                <Box component="span" fontSize="20px">+</Box> Add Bucket ARN
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </FieldArray>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                    <Box mt={4} display="flex" justifyContent="flex-end">
                                        <Button type="submit" variant="contained" color="primary">
                                            Submit S3 Config
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box textAlign="center" mt={4}>
                                    <Typography variant="h5" color="textPrimary">
                                        S3 Configuration Submitted for Onboarding
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" mt={2}>
                                        Engineering will review the configuration, which takes a few minutes.
                                        Please check back shortly for the S3 information once it has been onboarded.
                                    </Typography>
                                </Box>
                            )}
                        </form>
                    )}
                </Formik>
            )}
        </Paper>
    );
};

export default AWSOnboardingWizard;
